import { SupportedCommunicationPlatforms } from "fe-common/commonTypes/communicationLambdaApi";
import { sendMessageWithCommunicationService } from "./sendMessageWithCommmunicationService";
import { MessageTouchPoint } from "@kahunmedical/common-configuration/dist/configuration/messageTemplate";
import { localeSettings } from "../clinicSettings/localeDefaults";
import {
  ATHENA_GENDER_FEMALE,
  ATHENA_GENDER_MALE,
} from "../special/importMaccabiPatients/patientVisitData";

export enum InviteStatus {
  NotSentStatus,
  SentStatus,
  ErrorStatus,
  SendingStatus,
}

export interface InviteStatusList {
  SupportedCommunicationPlatforms?: {
    status: InviteStatus;
    errMsg: string;
  };
}

export interface InvitePatientData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  gender?: typeof ATHENA_GENDER_MALE | typeof ATHENA_GENDER_FEMALE;
  age?: number;
  caseUrl?: string;
}

export interface InviteData {
  id?: string;
  patient: InvitePatientData;
  visitDateTime: number;
  statusList?: InviteStatusList;
  errMsg?: string;
  isNew?: boolean;
}

export type SendInvitationProps = {
  method: SupportedCommunicationPlatforms;
  clinicName: string;
  clinicDisplayName: string;
  local?: string;
  inviteData: InviteData;
};

export type InvitePromiseData = {
  success: boolean;
  errMsg: string;
};

export const sendInvitation = (props: SendInvitationProps) =>
  new Promise(async (resolve: (v: InvitePromiseData) => void, reject) => {
    const { patient } = props.inviteData;
    sendMessageWithCommunicationService(props.clinicName, props.method, {
      touchPoint: MessageTouchPoint.SendConversationLink,
      to:
        props.method === SupportedCommunicationPlatforms.Whatsapp ||
        props.method === SupportedCommunicationPlatforms.SMS
          ? addCountryCode(patient.phoneNumber, props.local)
          : patient.email,
      locale: props.local,
      macroFields: InviteMacroFields(
        props.inviteData,
        props.clinicDisplayName,
        props.local,
      ),
    })
      .then(async (response) => ({
        response,
        text: await response.text(),
      }))
      .then(({ response, text }) =>
        resolve({
          success: response.status === 200,
          errMsg: text,
        }),
      )
      .catch((e) => {
        resolve({
          success: false,
          errMsg: e.message,
        });
      });
  });

const InviteMacroFields = (
  invite: InviteData,
  clinicName: string,
  clinicLocale: string,
) => {
  const dateLocale = localeSettings[clinicLocale]?.locale || "en-US";
  return {
    patient_first_name: invite.patient.firstName,
    patient_last_name: invite.patient.lastName,
    patient_full_name: [invite.patient.firstName, invite.patient.lastName].join(
      " ",
    ),
    case_url_text:
      clinicLocale === "he"
        ? (invite.patient.gender === "F" ? "לחצי" : "לחץ") + " כאן"
        : "Click here",
    visit_date: invite.visitDateTime
      ? new Intl.DateTimeFormat(dateLocale, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
        })
          .format(invite.visitDateTime)
          .replaceAll("/", "//")
      : "",
    case_url: invite.patient.caseUrl,
    clinic_name: clinicName,
    patient_code_id: invite.patient.caseUrl?.split?.("/")?.[4] || "",
  };
};

const addCountryCode = (phoneNumber: string, clinicLocale: string) => {
  const number = phoneNumber.replaceAll("-", "");
  if (number.startsWith("+")) return phoneNumber;

  const countryCode = localeSettings[clinicLocale]?.countryCode
    ? "+" + localeSettings[clinicLocale]?.countryCode
    : "";
  if (number.startsWith("0")) return countryCode + number.slice(1);

  return countryCode + number;
};
